import { createRouter, createWebHistory } from "vue-router";
import NProgress from "nprogress";
import "nprogress/nprogress.css";
import { checkAuth } from "@/utils/auth";
import { forceScrollToTop } from "@/utils/scroll";

const MainLayout = () =>
  import(/* webpackChunkName: "layout-main" */ "@/layouts/MainLayout.vue");
const MobileLayout = () =>
  import(/* webpackChunkName: "layout-mobile" */ "@/layouts/MobileLayout.vue");

// 配置 NProgress
NProgress.configure({
  showSpinner: false,
  minimum: 0.3,
  trickleSpeed: 200,
  easing: "ease",
  speed: 500,
});

// 检测设备类型（临时方案：统一按移动端处理）
const isMobileDevice = () => {
  return true;
};

const routes = [
  {
    path: "/s/:shortCode?",
    name: "ShortLink",
    component: () => import("@/views/ShortLink.vue"),
  },

  {
    path: "/forbidden-country",
    name: "ForbiddenCountryPc",
    component: () => import("@/views/ForbiddenCountry.vue"),
    meta: { layout: "none" },
  },
  {
    path: "/m/forbidden-country",
    name: "ForbiddenCountryMobile",
    component: () => import("@/views/ForbiddenCountry.vue"),
    meta: { layout: "none" },
  },

  // 桌面端路由
  {
    path: "/",
    component: MainLayout,
    redirect: "/home",
    children: [
      {
        path: "home",
        name: "HomeDesktop",
        component: () => import("@/views/Home/index.vue"),
      },
      {
        path: "home/:code",
        name: "HomeWithCodeDesktop",
        component: () => import("@/views/Home/index.vue"),
      },
      {
        path: "game-list",
        name: "GameListDesktop",
        component: () => import("@/views/Game/index.vue"),
      },
      {
        path: "game/:id",
        name: "GameDetailDesktop",
        component: () => import("@/views/Game/GameDetail.vue"),
        props: true,
        meta: { requiresAuth: true },
      },
      // 共用页面
      {
        path: "deposit",
        name: "Deposit",
        component: () => import("@/views/Deposit/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "withdraw",
        name: "Withdraw",
        component: () => import("@/views/Withdraw/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "account",
        name: "Account",
        component: () => import("@/views/Account/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "records",
        name: "Records",
        component: () => import("@/views/Records/records-new.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "connection",
        name: "Connection",
        component: () => import("@/views/Connection/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "jackpot-aposta",
        name: "JackpotAposta",
        component: () => import("@/views/JackpotAposta/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "jackpot-aposta/registro",
        name: "JackpotApostaRegistro",
        component: () => import("@/views/JackpotAposta/Registro.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "aposta",
        name: "Aposta",
        component: () => import("@/views/Aposta/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "premiacao",
        name: "Premiacao",
        component: () => import("@/views/Premiacao/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "vip",
        name: "VIP",
        component: () => import("@/views/VIP/index.vue"),
        meta: {
          layout: "desktop",
          requiresAuth: true,
        },
      },
      {
        path: "bonus",
        name: "MyBonus",
        component: () => import("@/views/MyBonus/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "messages",
        name: "Messages",
        component: () => import("@/views/Messages/index.vue"),
        meta: {
          layout: "desktop",
        },
      },
      {
        path: "invite",
        name: "Invite",
        component: () => import("@/views/Invite/invite-new.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "inviteRewardRules",
        name: "InviteRewardRules",
        component: () => import("@/views/Invite/invite-reward-rules.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "commission",
        name: "Commission",
        component: () => import("@/views/Commission/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "activity",
        name: "Activity",
        component: () => import("@/views/Activity/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "user/profile",
        name: "UserProfile",
        component: () => import("@/views/UserProfile/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "privacy-policy",
        name: "PrivacyPolicy",
        component: () => import("@/views/PrivacyPolicy/index.vue"),
        meta: {
          layout: "desktop",
        },
      },
      {
        path: "terms-of-service",
        name: "TermsOfService",
        component: () => import("@/views/TermsOfService/index.vue"),
        meta: {
          layout: "desktop",
        },
      },
      {
        path: "responsible-gaming",
        name: "ResponsibleGaming",
        component: () => import("@/views/ResponsibleGaming/index.vue"),
        meta: {
          layout: "desktop",
        },
      },
      {
        path: "cash-back",
        name: "CashBackDesktop",
        component: () => import("@/views/CashBack/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "bank-cards",
        name: "BankCardListDesktop",
        component: () => import("@/views/Withdraw/bindInfo.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "phone-binding",
        name: "PhoneBinding",
        component: () => import("@/views/PhoneBinding/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "Atividades-Pinduoduo",
        name: "PinduoduoActivityDesktop",
        component: () => import("@/views/Activity/PinduoduoNew.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "Pinduoduo-RewardRules",
        name: "PinduoduoRewardRulesDesktop",
        component: () => import("@/views/Activity/rule-detail.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "Opinion",
        name: "OpinionDesktop",
        component: () => import("@/views/Opinion/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "HelpCenter",
        name: "HelpCenterDesktop",
        component: () => import("@/views/HelpCenter/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
    ],
  },
  // 移动端路由
  {
    path: "/m",
    component: MobileLayout,
    redirect: "/m/home",
    children: [
      {
        path: "home",
        name: "HomeMobile",
        component: () => import("@/views/Mobile/Home/index.vue"),
      },
      {
        path: "home/:code",
        name: "HomeWithCodeMobile",
        component: () => import("@/views/Mobile/Home/index.vue"),
      },
      {
        path: "game-list",
        name: "GameListMobile",
        component: () => import("@/views/Mobile/Game/index.vue"),
      },
      {
        path: "game-manufacturer",
        name: "GameManufacturerMobile",
        component: () => import("@/views/Mobile/Game/GameList.vue"),
      },
      {
        path: "manufacturer",
        name: "ManufacturerMobile",
        component: () => import("@/views/Mobile/Game/GameManufacturer.vue"),
      },
      {
        path: "game/:id",
        name: "GameDetailMobile",
        component: () => import("@/views/Mobile/Game/GameDetail.vue"),
        props: true,
        meta: { requiresAuth: true },
      },
      {
        path: "user-info",
        name: "UserInfoMobile",
        component: () => import("@/views/Mobile/UserInfo.vue"),
        meta: { requiresAuth: false },
      },
      {
        path: "game-search",
        name: "GameSearchMobile",
        component: () => import("@/views/Mobile/Game/search.vue"),
      },
      {
        path: "customer-service",
        name: "CustomerServiceMobile",
        component: () => import("@/views/Mobile/Message/CustomerService.vue"),
        meta: { requiresAuth: true },
      },
      // 共用页面
      {
        path: "deposit",
        name: "DepositMobile",
        component: () => import("@/views/Deposit/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "withdraw",
        name: "WithdrawMobile",
        component: () => import("@/views/Withdraw/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "depositWithdraw",
        name: "DepositWithdrawMobile",
        component: () => import("@/views/DepositWithdraw/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "withdraw-instructions",
        name: "WithdrawInstructionsMobile",
        component: () => import("@/views/DepositWithdraw/WithdrawInstructions.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "account",
        name: "AccountMobile",
        component: () => import("@/views/Account/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "records",
        name: "RecordsMobile",
        component: () => import("@/views/Records/records-new.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "connection",
        name: "ConnectionMobile",
        component: () => import("@/views/Connection/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "deposit-bonus",
        name: "DepositBonusMobile",
        component: () => import("@/views/Mobile/DepositBonus/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "jackpot-aposta",
        name: "JackpotApostaMobile",
        component: () => import("@/views/JackpotAposta/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "jackpot-aposta/registro",
        name: "JackpotApostaRegistroMobile",
        component: () => import("@/views/JackpotAposta/Registro.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "aposta",
        name: "ApostaMobile",
        component: () => import("@/views/Aposta/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "premiacao",
        name: "PremiacaoMobile",
        component: () => import("@/views/Premiacao/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "vip",
        name: "VIPMobile",
        component: () => import("@/views/VIP/index.vue"),
        meta: {
          layout: "mobile",
          // requiresAuth: true,
        },
      },
      {
        path: "bonus",
        name: "MyBonusMobile",
        component: () => import("@/views/MyBonus/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "messages",
        name: "MessagesMobile",
        component: () => import("@/views/Messages/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "invite",
        name: "InviteMobile",
        component: () => import("@/views/Invite/invite-new.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "inviteRewardRules",
        name: "InviteRewardRulesMobile",
        component: () => import("@/views/Invite/invite-reward-rules.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "commission",
        name: "CommissionMobile",
        component: () => import("@/views/Commission/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "user/profile",
        name: "UserProfileMobile",
        component: () => import("@/views/UserProfile/mobile-info.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "user/profile/edit",
        name: "UserProfileEditMobile",
        component: () => import("@/views/UserProfile/edit-perfil.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "privacy-policy",
        name: "PrivacyPolicyMobile",
        component: () => import("@/views/PrivacyPolicy/index.vue"),
        meta: {
          layout: "mobile",
        },
      },
      {
        path: "terms-of-service",
        name: "TermsOfServiceMobile",
        component: () => import("@/views/TermsOfService/index.vue"),
        meta: {
          layout: "mobile",
        },
      },
      {
        path: "responsible-gaming",
        name: "ResponsibleGamingMobile",
        component: () => import("@/views/ResponsibleGaming/index.vue"),
        meta: {
          layout: "mobile",
        },
      },
      {
        path: "bank-cards",
        name: "BankCardListMobile",
        component: () => import("@/views/BankCardlist/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "cash-back",
        name: "CashBackMobile",
        component: () => import("@/views/CashBack/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "activity-menu",
        name: "ActivityMenuMobile",
        component: () => import("@/views/Mobile/ActivityMenu/index.vue"),
        meta: {
          requiresAuth: false,
          layout: "mobile",
        },
      },
      {
        path: "activity-menu/vipClub",
        name: "ActivityMenuVipClubMobile",
        component: () => import("@/views/Mobile/ActivityMenu/vipClub.vue"),
        meta: {
          requiresAuth: false,
          layout: "mobile",
        },
      },
      {
        path: "activity-menu/cashback",
        name: "ActivityMenuCashBackMobile",
        component: () => import("@/views/Mobile/ActivityMenu/cashBack.vue"),
        meta: {
          requiresAuth: false,
          layout: "mobile",
        },
      },
      {
        path: "activity-menu/carnavalTriplo",
        name: "ActivityMenuCarnavalTriploMobile",
        component: () => import("@/views/Mobile/ActivityMenu/carnavalTriplo.vue"),
        meta: {
          requiresAuth: false,
          layout: "mobile",
        },
      },
      {
        path: "activity-menu/keepInter",
        name: "ActivityMenuKeepInterMobile",
        component: () => import("@/views/Mobile/ActivityMenu/keepInter.vue"),
        meta: {
          requiresAuth: false,
          layout: "mobile",
        },
      },
      {
        path: "activity-menu/downlodReward",
        name: "ActivityMenuDownlodRewardMobile",
        component: () => import("@/views/Mobile/ActivityMenu/downlodReward.vue"),
        meta: {
          requiresAuth: false,
          layout: "mobile",
        },
      },
      {
        path: "activity-menu/apostasBônus",
        name: "ActivityMenuApostasBonusMobile",
        component: () => import("@/views/Mobile/ActivityMenu/apostasBônus.vue"),
        meta: {
          requiresAuth: false,
          layout: "mobile",
        },
      },
      {
        path: "activity-menu/comissaoApostas",
        name: "ActivityMenuComissaoApostasMobile",
        component: () => import("@/views/Mobile/ActivityMenu/comissaoApostas.vue"),
        meta: {
          requiresAuth: false,
          layout: "mobile",
        },
      },
      {
        path: "activity-menu/inviteReward",
        name: "ActivityMenuInviteRewardMobile",
        component: () => import("@/views/Mobile/ActivityMenu/inviteReward.vue"),
        meta: {
          requiresAuth: false,
          layout: "mobile",
        },
      },
      {
        path: "activity-menu/rechargeReward",
        name: "ActivityMenuRechargeRewardMobile",
        component: () => import("@/views/Mobile/ActivityMenu/rechargeReward.vue"),
        meta: {
          requiresAuth: false,
          layout: "mobile",
        },
      },
      {
        path: "activity-menu/jiangchihd",
        name: "ActivityMenuJiangchihdMobile",
        component: () => import("@/views/Mobile/ActivityMenu/jiangchihd.vue"),
        meta: {
          requiresAuth: false,
          layout: "mobile",
        },
      },
      {
        path: "activity",
        name: "ActivityMobile",
        component: () => import("@/views/Activity/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "s/:shortCode",
        name: "ShortLinkMobile",
        component: () => import("@/views/ShortLink.vue"),
        meta: {
          layout: "mobile",
        },
      },
      {
        path: "phone-binding",
        name: "PhoneBindingMobile",
        component: () => import("@/views/PhoneBinding/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "Atividades-Pinduoduo",
        name: "PinduoduoActivityMobile",
        component: () => import("@/views/Activity/PinduoduoNew.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "Pinduoduo-RewardRules",
        name: "PinduoduoRewardRulesMobile",
        component: () => import("@/views/Activity/rule-detail.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "Opinion",
        name: "OpinionMobile",
        component: () => import("@/views/Opinion/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "HelpCenter",
        name: "HelpCenterMobile",
        component: () => import("@/views/HelpCenter/index.vue"),
        meta: {
          requiresAuth: false,
          layout: "mobile",
        },
      },
      {
        path: "GameClassify",
        name: "GameClassifyMobile",
        component: () => import("@/views/Mobile/Game/GameClassify.vue"),
        meta: {
          requiresAuth: false,
          layout: "mobile",
        },
      },
      {
        path: "GameHis",
        name: "GameHisMobile",
        component: () => import("@/views/Mobile/History/GameHis.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "OperationHis",
        name: "OperationHisMobile",
        component: () => import("@/views/Mobile/History/OperationHis.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "TransactionHis",
        name: "TransactionHisMobile",
        component: () => import("@/views/Mobile/History/TransactionHis.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "SwitchAccount",
        name: "SwitchAccountMobile",
        component: () => import("@/views/Mobile/SwitchAccount/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "Setting",
        name: "SettingMobile",
        component: () => import("@/views/Setting/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "Login",
        name: "LoginMobile",
        component: () => import("@/views/Mobile/Login/index.vue"),
        meta: {
          requiresAuth: false,
          layout: "mobile",
        },
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // 如果有保存的位置（浏览器前进/后退），则恢复
    if (savedPosition) {
      return savedPosition;
    }

    // 如果路由有 hash，滚动到对应元素
    if (to.hash) {
      return {
        el: to.hash,
        behavior: "smooth",
      };
    }

    // 如果路由有 meta.scrollToTop 为 false，保持当前位置
    if (to.meta.scrollToTop === false) {
      return false;
    }

    // 默认滚动到顶部
    return {
      top: 0,
      behavior: "smooth", // 平滑滚动
    };
  },
});

// 修改设备切换处理函数
const handleDeviceSwitch = (to: any) => {
  console.log("handleDeviceSwitch", to);
  const isMobile = isMobileDevice();
  const path = to.path;
  const isMobilePath = path.startsWith("/m/");
  console.log("isMobilePath", path);
  // 特殊路由不进行设备切换
  if (path === "/s/" || path === "/activity/Atividades-Pinduoduo") {
    return true;
  }

  // 构建完整的重定向对象，包含查询参数和哈希值
  const buildRedirectRoute = (newPath: string) => {
    const redirectRoute: any = {
      path: newPath,
    };

    // 保留查询参数
    if (to.query && Object.keys(to.query).length > 0) {
      redirectRoute.query = to.query;
    }

    // 保留哈希值
    if (to.hash) {
      redirectRoute.hash = to.hash;
    }

    return redirectRoute;
  };

  // 如果是移动设备访问桌面端路由，重定向到移动端
  if (isMobile && !isMobilePath) {
    return buildRedirectRoute(`/m${path}`);
  }

  // 如果是桌面设备访问移动端路由，重定向到桌面端
  if (!isMobile && isMobilePath) {
    return buildRedirectRoute(path.replace("/m", ""));
  }

  return true;
};

// 全局前置守卫
router.beforeEach((to, from, next) => {
  console.log("beforeEach", to, from);
  // 先检查设备类型并处理重定向
  const redirectPath = handleDeviceSwitch(to);
  console.log("redirectPath", redirectPath);
  if (redirectPath !== true) {
    next(redirectPath);
    return;
  }

  // 然后检查权限
  const agentid = sessionStorage.getItem('agentid');
  const token = localStorage.getItem('token');
  
  // 如果有 agentid，且 URL 中没有，且用户未登录（即未注册完成）
  if (agentid && !to.query.agentid && !token) {
    const nextQuery = { ...to.query, agentid };
    // 使用 replace 避免历史记录混乱
    next({ path: to.path, query: nextQuery, hash: to.hash, params: to.params, replace: true });
    return;
  }

  const result = checkAuth(to, from);
  if (result === true) {
    NProgress.start();
    next();
  } else {
    next(result);
  }
});

router.afterEach((to, from) => {
  NProgress.done();

  // 强制滚动到顶部（除了有hash的路由）
  if (!to.hash) {
    forceScrollToTop();
  }
});

// 监听窗口大小变化
window.addEventListener("resize", () => {
  const currentPath = router.currentRoute.value.path;
  const redirectPath = handleDeviceSwitch({ path: currentPath });
  if (redirectPath !== true) {
    router.replace(redirectPath);
  }
});

export default router;
